<template>
    <div>
        <q-table
            id="changeover-property-table"
            :rows="report"
            :columns="table.columns"
            :pagination="pagination"
            :hide-bottom="report.length > 0"
            :table-class="{ 'sticky-table': report.length > 0 }"
            row-key="id"
            class="no-shadow border-grey"
            @update:pagination="updatePagination($event)"
            :no-data-label="
                $t(
                    'No product properties are available during this timeframe, or the properties belong to different categories. Please ensure product properties are available for this facility.'
                )
            "
        >
            <template v-slot:header="props">
                <q-tr :props="props" v-if="report.length > 0">
                    <q-th
                        v-for="col in props.cols"
                        :key="col.name"
                        :props="props"
                    >
                        {{ col.label }}
                        <q-tooltip style="font-size: 14px">
                            {{ tooltipText[col.name] }}
                        </q-tooltip>
                    </q-th>
                </q-tr>
            </template>
            <template v-slot:body-cell-previousProperty="props">
                <q-td class="formatted-cell ellipsis">
                    {{ props.row.previousProperty }}
                </q-td>
            </template>
            <template v-slot:body-cell-nextProperty="props">
                <q-td class="formatted-cell ellipsis">
                    {{ props.row.nextProperty }}
                </q-td>
            </template>
        </q-table>
    </div>
</template>

<script>
import { convertDurationToMinutes } from '@utils/time.js';

export default {
    name: 'ChangeoverPropertyTable',
    emits: ['update:pagination'],
    props: {
        report: {
            type: Object,
            required: true
        },
        pagination: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            tooltipText: {
                productCategory: this.$t(
                    'The product category of this pair of properties'
                ),
                previousProperty: this.$t(
                    'The product property running on the line prior to the changeover'
                ),
                nextProperty: this.$t(
                    'The product property running on the line following the changeover'
                ),
                eventsCount: this.$t(
                    'The number of changeovers between this pair of product properties'
                ),
                averageDuration: this.$t(
                    'The average duration of a changeover between this pair of product properties'
                ),
                totalAccumulatedExpectedDuration: this.$t(
                    'The total expected duration of all changeovers between this pair of product properties'
                ),
                totalAccumulatedDuration: this.$t(
                    'The total actual duration of all changeovers between this pair of product properties'
                ),
                totalLoss: this.$t(
                    'The difference between the total expected and total actual duration of all changeovers between this pair of product properties'
                ),
                averageLoss: this.$t(
                    'The total loss averaged over the number of changeovers between this pair of product properties'
                )
            }
        };
    },
    computed: {
        table() {
            return {
                columns: [
                    {
                        name: 'productCategory',
                        field: 'productCategory',
                        label: this.$t('Product Category'),
                        align: 'left',
                        sortable: true
                    },
                    {
                        name: 'previousProperty',
                        field: 'previousProperty',
                        label: this.$t('Previous Property'),
                        align: 'left',
                        sortable: true
                    },
                    {
                        name: 'nextProperty',
                        field: 'nextProperty',
                        label: this.$t('Next Property'),
                        align: 'left',
                        sortable: true
                    },
                    {
                        name: 'eventsCount',
                        field: 'eventsCount',
                        label: this.$t('Number of Events'),
                        align: 'left',
                        sortable: true
                    },
                    {
                        name: 'averageDuration',
                        field: 'averageDuration',
                        label: this.$t('Average Duration (min)'),
                        align: 'left',
                        sortable: true,
                        format: val => this.convertDurationToMinutes(val)
                    },
                    {
                        name: 'totalAccumulatedExpectedDuration',
                        field: 'totalAccumulatedExpectedDuration',
                        label: this.$t('Total Expected Duration (min)'),
                        align: 'left',
                        sortable: true,
                        format: val => this.convertDurationToMinutes(val)
                    },
                    {
                        name: 'totalAccumulatedDuration',
                        field: 'totalAccumulatedDuration',
                        label: this.$t('Total Accumulated Duration (min)'),
                        align: 'left',
                        sortable: true,
                        format: val => this.convertDurationToMinutes(val)
                    },
                    {
                        name: 'totalLoss',
                        field: 'totalLoss',
                        label: this.$t('Total Loss (min)'),
                        align: 'left',
                        sortable: true,
                        format: val => this.convertDurationToMinutes(val)
                    },
                    {
                        name: 'averageLoss',
                        field: 'averageLoss',
                        label: this.$t('Average Loss (min)'),
                        align: 'left',
                        sortable: true,
                        format: val => this.convertDurationToMinutes(val)
                    }
                ]
            };
        }
    },
    methods: {
        convertDurationToMinutes,
        /**
         * Emits an event to update the pagination state.
         * @param {Object} pagination - The new pagination data to be emitted.
         */
        updatePagination(pagination) {
            this.$emit('update:pagination', pagination);
        }
    }
};
</script>

<style lang="scss" scoped>
:deep(.q-table__top) {
    background-color: $secondary-white;
}
:deep(.formatted-cell) {
    min-width: 250px;
    max-width: 250px;
}
</style>
